import React from 'react'
import { ListPageLayout } from '@/shared/components/entities/EntityListLayout'
import { 
  DataCard, 
  DataTable, 
  DataTableWithCustomization,
  AdvancedFiltersModal, 
  QuickFiltersButton,
  BulkActionsBar,
  CustomizableCard,
  type DataTableColumn,
  type FilterField,
  type BulkAction,
  type CardField
} from '@/shared/components/entities'
import { EmptyState } from '@/shared/components/ui'
import { Modal, ConfirmDialog } from '@/shared/components/ui'
import OverflowMenu from '@/shared/components/ui/OverflowMenu'
import EntryActions from '@/modules/billing/entries/EntryActions'
import { useEntityListPage, type EntityListPageConfig } from '@/shared/components/entities/useEntityListPage'
import { useTableCustomization, type TableCustomizationController } from '@/shared/hooks/useTableCustomization'
import { useCardCustomization, type CardCustomizationController, type CardTemplate } from '@/shared/hooks/useCardCustomization'
import { usePermissions } from '@/modules/units/usePermissions'
import { TableCustomizationMenu, CardCustomizationMenu } from '@/shared/components/entities'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { Role } from '@/lib/permissions/types'
import { useUnit } from '@/modules/units/contexts/UnitContext'

// Configuração simplificada para renderização automática de cards
export interface CardConfig<TEntity> {
  // Extrai o ícone da entidade
  getIcon: (entity: TEntity) => React.ReactNode
  // Extrai o título
  getTitle: (entity: TEntity) => string
  // Extrai o subtítulo (opcional)
  getSubtitle?: (entity: TEntity) => string
  // Extrai o badge (opcional)
  getBadge?: (entity: TEntity) => {
    label: React.ReactNode
    variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
    icon?: React.ReactNode
  } | null
  // Área visual do card para templates com imagem/mídia.
  getMedia?: (entity: TEntity) => React.ReactNode
  // Define os campos do card
  fields: Array<{
    key: string
    label: string
    getValue: (entity: TEntity) => string | React.ReactNode
    isVisible?: (entity: TEntity) => boolean
  }>
  // Labels dos botões de ação
  editLabel?: string
  deleteLabel?: string
  // Ações extras injetadas entre Editar e Excluir
  getExtraActions?: (entity: TEntity) => Array<{
    label: string
    icon?: React.ReactNode
    onClick: () => void
    variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  }>
  // Texto de carimbo exibido sobre o card quando definido (ex: 'PAGO')
  getStamp?: (entity: TEntity) => string | null
  // Footer customizado (opcional)
  getFooter?: (entity: TEntity) => React.ReactNode
  // Classe CSS opcional para o wrapper do ícone do card (ex: cores por tipo)
  getIconWrapperClass?: (entity: TEntity) => string
}

// Função helper para renderizar automaticamente um card usando CustomizableCard
function renderAutoCard<TEntity>(
  entity: TEntity,
  onEdit: () => void,
  onDelete: () => void,
  cardConfig: CardConfig<TEntity>,
  cardCustomization: CardCustomizationController | null,
  canEdit: boolean,
  canDelete: boolean,
  extraProps?: Record<string, any>
): React.ReactNode {
  // Se n\u00e3o h\u00e1 customiza\u00e7\u00e3o, usar DataCard simples
  if (!cardCustomization) {
        const footerNode = cardConfig.getFooter?.(entity)
        return (
          <DataCard
            icon={cardConfig.getIcon(entity)}
            iconWrapperClass={cardConfig.getIconWrapperClass?.(entity)}
            title={cardConfig.getTitle(entity)}
            subtitle={cardConfig.getSubtitle?.(entity)}
            badge={cardConfig.getBadge?.(entity) || undefined}
            media={cardConfig.getMedia?.(entity)}
            fields={cardConfig.fields
              .filter(f => f.isVisible ? f.isVisible(entity) : true)
              .map(f => ({
              label: f.label,
              value: f.getValue(entity)
            }))}
            actions={[
              ...(canEdit ? [{
                label: cardConfig.editLabel || 'Editar',
                icon: (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                ),
                onClick: onEdit
              }] : []),
              ...(cardConfig.getExtraActions?.(entity) ?? []),
              ...(canDelete ? [{
                label: cardConfig.deleteLabel || 'Excluir',
                icon: (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                ),
                onClick: onDelete,
                variant: 'danger' as const
              }] : [])
            ]}
            actionsNode={footerNode}
            footer={undefined}
            stamp={cardConfig.getStamp?.(entity) ?? undefined}
            {...(extraProps || {})}
          />
        )
  }

  // Com customiza\u00e7\u00e3o, usar CustomizableCard
  const visibleFields = cardConfig.fields.filter(f => f.isVisible ? f.isVisible(entity) : true)
  const fields: CardField[] = visibleFields.map(f => ({
    key: f.key,
    label: f.label,
    value: f.getValue(entity)
  }))

  // Criar mapa de dados da entidade para configuração avançada
  const entityData = visibleFields.reduce((acc, f) => {
    acc[f.key] = String(f.getValue(entity) || '')
    return acc
  }, {} as Record<string, string>)

  // Computar título, subtítulo e badge baseado em modo avançado ou padrão
  const advancedConfig = cardCustomization.settings.advanced
  const isAdvancedMode = advancedConfig?.enabled

  const title = isAdvancedMode && advancedConfig.title
    ? cardCustomization.renderAdvancedSection(advancedConfig.title, entityData)
    : cardConfig.getTitle(entity)

  const subtitle = isAdvancedMode && advancedConfig.subtitle
    ? cardCustomization.renderAdvancedSection(advancedConfig.subtitle, entityData)
    : cardConfig.getSubtitle?.(entity)

  // Badge pode vir do modo avançado ou do cardConfig padrão
  let badge: { label: React.ReactNode; variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info'; icon?: React.ReactNode } | undefined
  if (isAdvancedMode && advancedConfig.badge) {
    const badgeValue = entityData[advancedConfig.badge.fieldKey]
    if (badgeValue) {
      badge = {
        label: badgeValue,
        variant: advancedConfig.badge.variant
      }
    }
  } else {
    badge = cardConfig.getBadge?.(entity) || undefined
  }

  const actions = [
    ...(canEdit ? [{
      label: cardConfig.editLabel || 'Editar',
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      onClick: onEdit
    }] : []),
    ...(cardConfig.getExtraActions?.(entity) ?? []),
    ...(canDelete ? [{
      label: cardConfig.deleteLabel || 'Excluir',
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
      onClick: onDelete,
      variant: 'danger' as const
    }] : [])
  ]

  const footerNode = cardConfig.getFooter?.(entity)
  const customizableNode = (
    <CustomizableCard
      icon={cardConfig.getIcon(entity)}
      title={title}
      subtitle={subtitle}
      badge={badge}
      media={cardConfig.getMedia?.(entity)}
      fields={fields}
      actions={actions}
      actionsNode={footerNode}
      footer={undefined}
      customization={cardCustomization.settings}
      sizeClasses={cardCustomization.getSizeClasses()}
      getFontSizeClass={cardCustomization.getFontSizeClass}
      getFieldClasses={cardCustomization.getFieldClasses}
    />
  )
  // Wrap CustomizableCard with stamp overlay
  const stamp = cardConfig.getStamp?.(entity)
  if (!stamp) return customizableNode
  const isListView = cardCustomization.settings.template === 'list-view'
  return (
    <div className="relative overflow-hidden rounded-lg">
      {customizableNode}
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg">
        <div className="absolute inset-0 flex items-center justify-center" style={{ transform: isListView ? 'none' : 'rotate(-32deg)' }}>
          <span
            aria-hidden
            className="block font-black uppercase select-none text-emerald-600 dark:text-emerald-400"
            style={{
              width: isListView ? 'auto' : undefined,
              textAlign: 'center',
              whiteSpace: 'nowrap',
              fontSize: isListView ? 'clamp(0.85rem, 2vw, 1.1rem)' : 'clamp(1.5rem, 6vw, 2.2rem)',
              letterSpacing: '0.4em',
              opacity: 0.35,
              padding: isListView ? '2px 10px' : '4px 18px',
              border: '3px solid currentColor',
              outline: '1px solid currentColor',
              outlineOffset: '-6px',
              borderRadius: '2px',
            }}
          >
            {stamp}
          </span>
        </div>
      </div>
    </div>
  )
}

export interface EntityListPageProps<TEntity, TInput> {
  config: EntityListPageConfig<TEntity, TInput>
  
  // Role do usuário para validações de permissão
  userRole?: Role | null
  
  // Labels e textos
  labels: {
    title: string
    description: string
    newButton: string
    searchPlaceholder: string
    emptyTitle: string
    emptyDescription: string
    emptyActionLabel?: string
    modalTitleNew: string
    modalTitleEdit: string
    modalTitleView?: string
    recordTitle?: string
    deleteTitle: string
    deleteMessage: string
    deleteButton: string
    cancelButton: string
    submitButton: string
    updateButton: string
    viewCard: string
    viewTable: string
    clearSearch: string
    bulkDeleteTitle?: string
    bulkDeleteMessage?: string
    bulkDeleteButton?: string
    bulkExportButton?: string
  }
  
  // Ícone da página
  icon: React.ReactNode
  
  // Campos de filtro
  filterFields: FilterField[]
  
  // Colunas da tabela
  tableColumns: DataTableColumn<TEntity>[]
  
  // Configuração da coluna de ações (opcional)
  showActionsColumn?: boolean  // Default: true
  actionsColumnLabel?: string  // Default: labels.actions ou 'Ações'
  canEdit?: (entity: TEntity) => boolean  // Default: sempre true
  canDelete?: (entity: TEntity) => boolean  // Default: sempre true
  // Ações extras no menu ... da linha (inseridas antes de Excluir)
  extraRowActions?: (entity: TEntity) => Array<{
    label: string
    icon?: React.ReactNode
    onClick: () => void
    danger?: boolean
  }>
  // Ações extras no menu ... do modal de visualização (inseridas antes de Excluir)
  extraViewActions?: (entity: TEntity) => Array<{
    label: string
    icon?: React.ReactNode
    onClick: () => void
    danger?: boolean
  }>
  // Botão de ação rápida inline (aparece antes do menu ...)
  rowQuickAction?: (entity: TEntity) => { icon: React.ReactNode; label: string; onClick: () => void } | null
  // Optional handler to allow parent pages to handle undo payment from `EntryActions`
  onUndoPayment?: (entry: any) => void
  
  // Bulk selection configuration (opcional)
  enableBulkSelection?: boolean  // Default: true
  bulkActions?: BulkAction[]  // Ações customizadas em lote
  hideBulkDelete?: boolean     // Oculta o botão padrão de deletar em lote
  onBulkExport?: (ids: string[]) => Promise<void>  // Handler de exportação
  onBulkCustomAction?: { label: string; icon?: React.ReactNode; variant?: 'primary' | 'secondary' | 'danger'; handler: (ids: string[]) => Promise<void> }
  
  // Table customization configuration (opcional)
  enableTableCustomization?: boolean  // Default: true
  tableCustomizationStorageKey?: string  // Chave única para persistência (default: entityType)
  
  // Card customization configuration (opcional)
  enableCardCustomization?: boolean  // Default: true (ativa quando cardFields é definido)
  cardFields?: string[]  // Lista de chaves de campos disponíveis para customização
  cardCustomizationStorageKey?: string  // Chave única para persistência (default: entityType + '-cards')
  defaultCardTemplate?: CardTemplate  // Template padrão: 'compact' | 'image-focus' | 'details-rich'
  preferDefaultCardTemplate?: boolean
  viewModeStorageKey?: string
  defaultViewMode?: 'card' | 'table'
  preferDefaultViewMode?: boolean
  
  // Configuração simplificada para renderização automática de cards (NOVO!)
  cardConfig?: CardConfig<TEntity>  // Se definido, renderCard se torna opcional
  
  // Renderização do card (opcional se cardConfig for definido)
  renderCard?: (
    entity: TEntity, 
    onEdit: (entity: TEntity) => void, 
    onDelete: (entity: TEntity) => void,
    cardCustomization?: CardCustomizationController  // Parâmetro opcional para customização
  ) => React.ReactNode
  
  // Renderização do formulário
  renderForm: (
    formData: TInput,
    setFormData: (data: TInput) => void,
    formErrors: Record<string, string>,
    submitting: boolean,
    editingEntity: TEntity | null,
    mode: 'create' | 'edit' | 'view'
  ) => React.ReactNode
  
  // Função para inicializar formData a partir de uma entidade (para edição)
  entityToFormData: (entity: TEntity) => TInput
  
  // Função para criar formData vazio (para novo registro)
  emptyFormData: () => TInput
  // Opcional: callback chamado quando um card é movido no Kanban
  onKanbanMove?: (entity: TEntity, newColumnValue: string) => Promise<void> | void
  // Optional translation namespace for route-specific translations (e.g. 'settings.assets.categories')
  namespace?: string
  // Filtros rápidos fixos exibidos como pills acima do conteúdo
  presetFilters?: Array<{
    key:          string
    label:        string
    icon?:        React.ReactNode
    filterValues: Record<string, any>
    activeClass?: string  // classes CSS quando ativo (substitui o padrão verde)
  }>
  // Conteúdo extra renderizado antes dos preset chips na faixa sticky (ex: navegador de mês + tabs de status)
  chipsLeading?: React.ReactNode
  // Totalizador discreto exibido ao lado do contador de resultados
  renderSummary?: (entities: TEntity[]) => React.ReactNode
  // Abre o modal de criação automaticamente ao montar (ex: atalho do dashboard)
  initialOpenCreate?: boolean
  // Abre o modal de edição automaticamente ao montar com a entidade fornecida
  initialEditEntity?: TEntity | null
  // Modo headless: renderiza apenas o Modal, sem a lista (ex: atalho do dashboard que não navega)
  headless?: boolean
  // Callback chamado quando o modal fecha no modo headless
  onHeadlessClose?: () => void
  // Optional callback invoked when row action components (like EntryActions) confirm an action
  onActionDone?: (action: string, entity: any) => void
}

export function EntityListPage<TEntity extends { id: string }, TInput>({
  config,
  labels,
  icon,
  filterFields,
  tableColumns,
  showActionsColumn = true,
  actionsColumnLabel,
  canEdit = () => true,
  canDelete = () => true,
  extraRowActions,
  extraViewActions,
  rowQuickAction,
  userRole,
  enableBulkSelection = true,
  bulkActions,
  hideBulkDelete = false,
  onBulkExport,
  onBulkCustomAction,
  enableTableCustomization = true,
  tableCustomizationStorageKey,
  enableCardCustomization = true,
  cardFields = [],
  cardCustomizationStorageKey,
  defaultCardTemplate = 'compact',
  preferDefaultCardTemplate = false,
  viewModeStorageKey,
  defaultViewMode,
  preferDefaultViewMode,
  cardConfig,
  renderCard,
  renderForm,
  entityToFormData,
  emptyFormData
  , onKanbanMove, namespace, presetFilters, chipsLeading, renderSummary, initialOpenCreate, initialEditEntity, headless = false, onHeadlessClose, onActionDone, onUndoPayment
}: EntityListPageProps<TEntity, TInput>) {
  // Validação: renderCard OU cardConfig deve ser fornecido
  if (!renderCard && !cardConfig) {
    throw new Error('EntityListPage: Você deve fornecer renderCard ou cardConfig')
  }
  const { userRole: contextUserRole } = useUnit()
  const effectiveUserRole = userRole || contextUserRole
  const permissions = usePermissions(effectiveUserRole)
  
  const resolvedCanEdit = !permissions.canEditEntries ? () => false : canEdit
  const resolvedCanDelete = !permissions.canDeleteEntries ? () => false : canDelete
  const {
    filteredEntities,
    loading,
    submitting,
    error,
    success,
    searchQuery,
    setSearchQuery,
    viewMode,
    setViewMode,
    setViewModeTemporary,
    selectedIds,
    isAllSelected,
    isSomeSelected,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    showModal,
    modalMode,
    showAdvancedFilters,
    setShowAdvancedFilters,
    editingEntity,
    confirmDelete,
    setConfirmDelete,
    confirmBulkDelete,
    setConfirmBulkDelete,
    formData,
    setFormData,
    formErrors,
    filterValues,
    setFilterValues,
    savedFilters,
    activeFiltersCount,
    handleOpenModal,
    handleCloseModal,
    handleSubmit,
    handleDelete,
    handleBulkDelete,
    handleClearFilters,
    saveFilter,
    loadFilter,
    deleteFilter
  } = useEntityListPage<TEntity, TInput>({
    ...config,
    viewModeStorageKey,
    defaultViewMode,
    preferDefaultViewMode,
  })

  const { t } = useTranslation()

  const isEntitySelectable = (entity: TEntity) => {
    // Se usuário não tem permissão para deletar, nada é selecionável
    if (!permissions.canDeleteEntries) return false
    
    try {
      return typeof (config as any).isEntitySelectable === 'function' ? (config as any).isEntitySelectable(entity) : true
    } catch (err) {
      return true
    }
  }

  const selectableFilteredEntities = filteredEntities.filter(e => isEntitySelectable(e))
  const hasSelectableItems = selectableFilteredEntities.length > 0

  // Auto-open create modal on mount when requested (e.g. from dashboard shortcut)
  React.useEffect(() => {
    if (initialEditEntity) handleOpenModal(initialEditEntity as any, 'edit')
    else if (initialOpenCreate) handleOpenModal()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // In headless mode, intercept close to also notify parent
  const handleClose = React.useCallback(() => {
    handleCloseModal()
    if (headless) onHeadlessClose?.()
  }, [handleCloseModal, headless, onHeadlessClose])

  // Active preset quick-filter key
  const [activePresetKey, setActivePresetKey] = React.useState<string | null>(null)

  const applyPreset = (key: string, values: Record<string, any>) => {
    if (activePresetKey === key) {
      // toggle off
      setActivePresetKey(null)
      handleClearFilters()
    } else {
      setActivePresetKey(key)
      setFilterValues(values)
    }
  }

  const clearPresetAndFilters = () => {
    setActivePresetKey(null)
    handleClearFilters()
  }

  // Adicionar coluna de ações automaticamente
  const actionsColumn: DataTableColumn<TEntity> = {
    key: 'actions',
    label: actionsColumnLabel || 'Ações',
    align: 'right',
    render: (entity) => {
      const showEdit = resolvedCanEdit(entity)
      const entityActiveFlag = (entity as any)?.is_active
      const showDelete = resolvedCanDelete(entity) && (entityActiveFlag === undefined || !!entityActiveFlag)
      
      if (!showEdit && !showDelete && !extraRowActions?.(entity)?.length && !rowQuickAction?.(entity)) {
        return (
          <div className="flex items-center justify-end">
            <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
          </div>
        )
      }

      const quickAction = rowQuickAction?.(entity) ?? null

      // If this page is the billing entries list, render EntryActions so modals are owned
      // and behave identically to the dashboard/card views.
      if ((namespace || '') === 'billing.entries') {
        return (
          <div className="flex items-center justify-end">
            <EntryActions entry={entity as any} isMobile={isMobile} isMember={!permissions.canEditEntries} onActionDone={onActionDone as any} onUndoPayment={onUndoPayment as any} />
          </div>
        )
      }

      return (
        <div className="flex items-center justify-end gap-0.5">
          {quickAction && (
            <button
              onClick={(e) => { e.stopPropagation(); quickAction.onClick() }}
              title={quickAction.label}
              className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20 transition-colors"
            >
              {quickAction.icon}
            </button>
          )}
          <OverflowMenu
            items={[
              // Selection toggle
              {
                label: t('list.selectItemGeneric') || 'Marcar',
                checkable: true,
                checked: selectedIds.has((entity as any).id),
                onToggle: () => toggleSelection((entity as any).id)
              },
              // Edit
              ...(showEdit ? [{
                label: t('actions.edit') || 'Editar',
                icon: (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                ),
                onClick: () => { /* stopPropagation handled by OverflowMenu */ handleOpenModal(entity) }
              }] : []),
              // Extra actions injected by the parent
              ...(extraRowActions?.(entity) ?? []),
              // Delete
              ...(showDelete ? [{
                label: labels.deleteButton || t('actions.delete') || 'Excluir',
                icon: (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                ),
                danger: true,
                onClick: () => setConfirmDelete(entity)
              }] : [])
            ]}
          />
        </div>
      )
    }
  }

  // Ensure 'actions' column is always the last column when enabled.
  const baseTableColumns = tableColumns.filter(c => c.key !== 'actions')
  const resolvedTableColumns = showActionsColumn
    ? [...baseTableColumns, actionsColumn]
    : baseTableColumns

  // Controller de customização da tabela (lifted para o pai para permitir controlar o menu a partir do header)
  const tableCustomizationKey = tableCustomizationStorageKey || `${config.entityType}-table`
  const tableCustomizationController: TableCustomizationController = useTableCustomization({
    storageKey: tableCustomizationKey,
    defaultColumns: resolvedTableColumns.map(c => c.key)
  })

  // Controller de customização de cards (opcional)
  const cardCustomizationKey = cardCustomizationStorageKey || `${config.entityType}-cards`
  // Se cardConfig é definido e cardFields não, extrair automaticamente os campos
  const resolvedCardFields = React.useMemo(() => {
    if (cardFields.length > 0) return cardFields
    if (cardConfig) return cardConfig.fields.map(f => f.key)
    return []
  }, [cardFields, cardConfig])

  const cardCustomizationController: CardCustomizationController | null = enableCardCustomization && resolvedCardFields.length > 0
    ? useCardCustomization({
        storageKey: cardCustomizationKey,
        defaultFields: resolvedCardFields,
        defaultTemplate: defaultCardTemplate,
        preferDefaultTemplate: preferDefaultCardTemplate
      })
    : null

  // Estado local para movimentações Kanban (apenas UI)
  const [kanbanMovedGroups, setKanbanMovedGroups] = React.useState<Record<string, string>>({})
  const [isMobile, setIsMobile] = React.useState(false)
  const [showOnlySelected, setShowOnlySelected] = React.useState(false)

  // Auto-clear when nothing is selected
  React.useEffect(() => {
    if (selectedIds.size === 0) setShowOnlySelected(false)
  }, [selectedIds.size])

  const displayedEntities = showOnlySelected && selectedIds.size > 0
    ? filteredEntities.filter(e => selectedIds.has(e.id))
    : filteredEntities
  const showingLabel = displayedEntities.length === 1
    ? t('list.showingOne', { count: String(displayedEntities.length) })
    : t('list.showingMany', { count: String(displayedEntities.length) })

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Colunas preparadas para o menu no header, respeitando a ordem salva
  const headerMenuColumns = resolvedTableColumns
    .map(col => ({
      key: col.key,
      label: col.label,
      visible: tableCustomizationController.settings.columns[col.key]?.visible !== false,
      order: tableCustomizationController.settings.columns[col.key]?.order ?? 9999
    }))
    .sort((a, b) => a.order - b.order)
    .map(({ order: _order, ...rest }) => rest)

  // Inicializar formData quando modal abrir
  React.useEffect(() => {
    let t: number | null = null
    if (showModal) {
      // Defer initialization to next tick to avoid race conditions
      t = window.setTimeout(() => {
        try {
          if (editingEntity) setFormData(entityToFormData(editingEntity))
          else setFormData(emptyFormData())
        } catch (err) {
          console.error('Failed to initialize form data for modal:', err)
        }
      }, 0)
    }
    return () => { if (t != null) window.clearTimeout(t) }
  }, [showModal, editingEntity])

  // Default bulk actions
  const defaultBulkActions: BulkAction[] = [
    ...(bulkActions || []),
    ...(onBulkExport ? [{
      key: 'export',
      label: labels.bulkExportButton || t('list.exportSelected'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      ),
      onClick: async () => {
        const selectedIdsArray = Array.from(selectedIds)
        await onBulkExport(selectedIdsArray)
      }
    }] : []),
    ...(onBulkCustomAction ? [{
      key: 'custom',
      label: onBulkCustomAction.label,
      icon: onBulkCustomAction.icon,
      variant: onBulkCustomAction.variant,
      onClick: async () => {
        const selectedIdsArray = Array.from(selectedIds)
        await onBulkCustomAction.handler(selectedIdsArray)
      }
    }] : []),
    ...(!hideBulkDelete ? [{
      key: 'delete',
      label: labels.bulkDeleteButton || t('list.deleteSelected'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
      variant: 'danger' as const,
      onClick: () => setConfirmBulkDelete(true)
    }] : []),
  ]

  // Menu de customização unificado baseado no viewMode
  const customizationMenu = React.useMemo(() => {
    // Para tabela: mostrar menu de customização de tabela
    if (viewMode === 'table' && enableTableCustomization) {
      return (
        <TableCustomizationMenu
          columns={headerMenuColumns}
          density={tableCustomizationController.settings.density}
          onToggleColumn={tableCustomizationController.toggleColumn}
          onReorderColumns={tableCustomizationController.reorderColumns}
          onChangeDensity={tableCustomizationController.setDensity}
          onReset={tableCustomizationController.resetToDefaults}
        />
      )
    }
    
    // Para cards: mostrar menu de customização de cards (se habilitado)
    if (viewMode === 'card' && enableCardCustomization && cardCustomizationController) {
      // Ordenar campos pela ordem salva no settings
      const orderedFieldKeys = Object.keys(cardCustomizationController.settings.fields)
        .sort((a, b) => 
          cardCustomizationController.settings.fields[a].order - 
          cardCustomizationController.settings.fields[b].order
        )
      
      return (
        <CardCustomizationMenu
          fields={orderedFieldKeys.map(key => {
            // Se cardConfig existe, pegar label dos fields
            const fieldConfig = cardConfig?.fields.find(f => f.key === key)
            return {
              key,
              label: fieldConfig?.label || key,
              visible: cardCustomizationController.settings.fields[key]?.visible !== false
            }
          })}
          template={cardCustomizationController.settings.template}
          size={cardCustomizationController.settings.size}
          titleFontSize={cardCustomizationController.settings.titleFontSize}
          subtitleFontSize={cardCustomizationController.settings.subtitleFontSize}
          onToggleField={cardCustomizationController.toggleField}
          onReorderFields={cardCustomizationController.reorderFields}
          onChangeTemplate={cardCustomizationController.setTemplate}
          onChangeSize={cardCustomizationController.setSize}
          onChangeTitleFontSize={cardCustomizationController.setTitleFontSize}
          onChangeSubtitleFontSize={cardCustomizationController.setSubtitleFontSize}
          onReset={cardCustomizationController.resetToDefaults}
          controller={cardCustomizationController}
        />
      )
    }
    
    return null
  }, [
    viewMode,
    enableTableCustomization,
    enableCardCustomization,
    cardCustomizationController,
    tableCustomizationController,
    headerMenuColumns,
    resolvedCardFields,
    cardConfig
  ])

  // Helper function to extract field value from entity using CardConfig if available
  const getFieldValue = (entity: TEntity, fieldKey: string): any => {
    // If cardConfig exists and has config for this field, use its getValue
    if (cardConfig) {
      const fieldConfig = cardConfig.fields.find(f => f.key === fieldKey)
      if (fieldConfig) {
        return String(fieldConfig.getValue(entity) || '')
      }
    }
    // Fallback to direct property access
    return String((entity as any)[fieldKey] ?? '')
  }

  // Card view rendering (separado para evitar IIFE no JSX)
  // Helper to render a card and inject bulk-selection props when the page
  // provided a custom `renderCard` (so custom renderers also get the checkbox).
  const renderCardNode = (entity: TEntity, cardCustomizationParam?: CardCustomizationController | null) => {
    const cardShowDelete = resolvedCanDelete(entity) && (((entity as any)?.is_active === undefined) || !!(entity as any).is_active)
    const node = renderCard
      ? renderCard(entity, () => handleOpenModal(entity), () => setConfirmDelete(entity), cardCustomizationParam || undefined)
      : renderAutoCard(
          entity,
          () => handleOpenModal(entity),
          () => setConfirmDelete(entity),
          cardConfig!,
          cardCustomizationParam || null,
          resolvedCanEdit(entity),
          cardShowDelete,
          enableBulkSelection ? {
            selectable: isEntitySelectable(entity),
            isSelected: selectedIds.has(entity.id),
            onToggleSelection: () => toggleSelection(entity.id)
          } : undefined
        )

    if (enableBulkSelection && React.isValidElement(node)) {
      try {
        return React.cloneElement(node as React.ReactElement, {
          selectable: isEntitySelectable(entity),
          isSelected: selectedIds.has(entity.id),
          onToggleSelection: () => toggleSelection(entity.id)
        })
      } catch (err) {
        // If cloneElement fails for any reason, just return original node
        return node
      }
    }

    return node
  }

  // Prefer `is_active` flag when deciding if the editing entity may be deleted
  const editingShowDelete = editingEntity
    ? (resolvedCanDelete(editingEntity) && (((editingEntity as any)?.is_active === undefined) || !!(editingEntity as any).is_active))
    : false

  const cardView = (() => {
    const isListView = cardCustomizationController?.settings.template === 'list-view'
    const gridClass = isListView
      ? 'flex flex-col rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden'
      : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'

    if (!cardCustomizationController) {
      // Render grid without customization controller
      return (
        <div className={gridClass}>
          {displayedEntities.map((entity) => {
            const renderedCard = renderCardNode(entity, null)

            // Wrap card so clicking the card opens VIEW mode (preserve internal action handlers)
            const wrapper = (
              <div
                key={entity.id}
                className={` ${enableBulkSelection && selectedIds.has(entity.id) ? 'ring-1 ring-emerald-200 dark:ring-emerald-600 rounded-lg' : ''} cursor-pointer`}
                onClick={() => handleOpenModal(entity, 'view')}
              >
                {renderedCard}
              </div>
            )

            return wrapper
          })}
        </div>
      )
    }

    const grouping = cardCustomizationController.settings.grouping
    const groupingField = grouping?.fieldKey

    if (grouping && grouping.mode === 'kanban' && groupingField) {
      const explicitCols = grouping.columns && grouping.columns.length > 0
        ? grouping.columns
        : Array.from(new Set(displayedEntities.map(e => getFieldValue(e, groupingField))))

      const handleDragStart = (e: React.DragEvent, id: string) => {
        e.dataTransfer.setData('text/plain', id)
      }

      const handleDropOnColumn = async (e: React.DragEvent, columnValue: string) => {
        e.preventDefault()
        const id = e.dataTransfer.getData('text/plain')
        if (!id) return

        // Atualiza estado UI imediato
        setKanbanMovedGroups(prev => ({ ...prev, [id]: columnValue }))

        // Tenta persistir via callback opcional `onKanbanMove` passado nas props
        try {
          const movedEntity = displayedEntities.find((ent) => (ent as any).id === id)
          if (movedEntity && typeof onKanbanMove === 'function') {
            await onKanbanMove(movedEntity, columnValue)
          }
        } catch (err) {
          console.error('Erro ao persistir movimentação Kanban:', err)
        }
      }

      return (
        <div className="flex gap-4 items-start overflow-x-auto">
          {explicitCols.map((col) => {
            if (displayedEntities.length > 0) {
              console.log(`[Kanban Debug] Column="${col}", Grouping field="${groupingField}", Explicit columns:`, explicitCols)
            }
            const columnEntities = displayedEntities.filter(entity => {
              const rawValue = getFieldValue(entity, groupingField)
              const current = kanbanMovedGroups[entity.id] ?? rawValue

              const columnLabel = String(col).trim()

              const valueMatchesColumn = (val: any, label: string) => {
                if (val === null || val === undefined || val === '') return label === ''

                // Normaliza e remove acentos para comparações mais robustas
                const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                const slabel = normalize(label || '')

                // Se é booleano, mapeia para palavras-chave
                if (typeof val === 'boolean') {
                  if (val === true) {
                    return ['ativo', 'active', 'true', 'sim', 'yes', '1', 'verdadeiro'].includes(slabel)
                  } else {
                    return ['inativo', 'inactive', 'false', 'nao', 'não', 'no', '0', 'falso'].includes(slabel)
                  }
                }

                // Para strings/números, normaliza o valor e tenta detectar valores booleanos representados como string
                const svalRaw = String(val || '')
                const sval = normalize(svalRaw)

                // Se o valor for uma string que representa booleano/flags, trate como booleano
                if (['true', 'false', '1', '0', 'sim', 'nao', 'nao', 'não', 'yes', 'no'].includes(sval)) {
                  if (['true', '1', 'sim', 'yes'].includes(sval)) {
                    return ['ativo', 'active', 'true', 'sim', 'yes', '1', 'verdadeiro'].includes(slabel)
                  }
                  return ['inativo', 'inactive', 'false', 'nao', 'não', 'no', '0', 'falso'].includes(slabel)
                }

                console.log(`[Kanban Debug] Comparando: val="${val}" (type=${typeof val}), label="${label}", sval="${sval}", slabel="${slabel}", match=${sval === slabel}`)
                return sval === slabel
              }

              return valueMatchesColumn(current, columnLabel)
            })

            return (
              <div key={col} className="min-w-[260px] bg-slate-50 dark:bg-slate-900/20 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
                <h4 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-100">{col}</h4>
                <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => handleDropOnColumn(e, col)} className="space-y-3 min-h-[80px]">
                  {columnEntities.map(entity => {
                    const renderedCard = renderCardNode(entity, cardCustomizationController)

                    const isSelected = selectedIds.has(entity.id)
                    return (
                      <div key={entity.id} draggable onDragStart={(e) => handleDragStart(e, entity.id)} className={`${isSelected ? 'ring-1 ring-emerald-200 dark:ring-emerald-600 rounded-lg' : ''}`}>
                        <div className="cursor-grab" onClick={() => handleOpenModal(entity, 'view')}>{renderedCard}</div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    // fallback
    return (
        <div className={gridClass}>
        {displayedEntities.map(entity => {
          const renderedCard = renderCardNode(entity, cardCustomizationController || undefined)
          return (
            <div key={entity.id} className="cursor-pointer" onClick={() => handleOpenModal(entity, 'view')}>
              {renderedCard}
            </div>
          )
        })}
      </div>
    )
  })()

  // Headless mode: render only the Modal (no list, no layout)
  if (headless) {
    return (
      <>
        <Modal
          isOpen={showModal}
          onClose={handleClose}
          title={editingEntity ? labels.modalTitleEdit : labels.modalTitleNew}
          size={isMobile ? 'xl' : 'lg'}
          fullScreen={isMobile}
          mobileTopAligned={isMobile}
          headerLeft={isMobile ? (
            <button
              onClick={handleClose}
              className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-colors"
              title={labels.cancelButton}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : undefined}
          headerRight={(
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const form = document.getElementById('entity-edit-form') as HTMLFormElement | null
                  if (form && typeof form.requestSubmit === 'function') form.requestSubmit()
                  else if (form) form.submit()
                }}
                className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 transition-colors"
                title={labels.submitButton}
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              {!isMobile && (
                <button
                  onClick={handleClose}
                  className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
                  title={labels.cancelButton}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}
        >
          <form id="entity-edit-form" onSubmit={handleSubmit}>
            {renderForm(formData, setFormData, formErrors, submitting, null, 'create')}
          </form>
        </Modal>
      </>
    )
  }

  return (
    <>
      <ListPageLayout
        title={labels.title}
        description={labels.description}
        icon={icon}
        onNew={!permissions.canEditEntries ? undefined : () => handleOpenModal()}
        newButtonLabel={labels.newButton}
        newButtonDisabled={submitting}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={labels.searchPlaceholder}
        clearSearchLabel={labels.clearSearch}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onMobileViewOverride={setViewModeTemporary}
        showViewToggle={true}
        viewCardLabel={labels.viewCard}
        viewTableLabel={labels.viewTable}
        loading={loading}
        error={error}
        success={success}
        customizationMenu={customizationMenu}
        titleRight={renderSummary ? renderSummary(displayedEntities) : undefined}
        stickyChips={(chipsLeading || (presetFilters && presetFilters.length > 0)) ? (
          <>
            {chipsLeading}
            {chipsLeading && presetFilters && presetFilters.length > 0 && (
              <span className="w-px h-5 bg-slate-200 dark:bg-slate-600 shrink-0 mx-1" />
            )}
            {presetFilters?.map((preset) => {
              const isActive = activePresetKey === preset.key
              return (
                <button
                  key={preset.key}
                  onClick={() => applyPreset(preset.key, preset.filterValues)}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    isActive
                      ? (preset.activeClass ?? 'bg-emerald-600 border-emerald-600 text-white shadow-sm')
                      : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500'
                  }`}
                >
                  {preset.icon && <span className="shrink-0">{preset.icon}</span>}
                  {preset.label}
                  {isActive && (
                    <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </button>
              )
            })}
          </>
        ) : undefined}
        searchActions={
          <QuickFiltersButton
            savedFilters={savedFilters}
            onLoadFilter={loadFilter}
            onClearFilters={() => { clearPresetAndFilters(); setShowOnlySelected(false) }}
            onOpenAdvancedFilters={() => setShowAdvancedFilters(true)}
            activeFiltersCount={activeFiltersCount + (activePresetKey ? 1 : 0)}
            selectedCount={selectedIds.size}
            showOnlySelected={showOnlySelected}
            onToggleShowSelected={() => setShowOnlySelected(prev => !prev)}
          />
        }
        stickyToolbar={!loading ? (
          <div className="flex items-center justify-between gap-3 px-1 py-0.5">
            <p className="min-w-0 text-sm text-slate-600 dark:text-slate-400 truncate">
              {showingLabel}
              {(searchQuery || activeFiltersCount > 0) && (
                <span className="ml-2 text-emerald-600 dark:text-emerald-400">
                  {t('list.filtered')}
                </span>
              )}
            </p>

            {enableBulkSelection && (
              <label className="shrink-0 inline-flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = isSomeSelected
                    }
                  }}
                  onChange={toggleSelectAll}
                  disabled={!hasSelectableItems || !permissions.canDeleteEntries}
                  className="w-4 h-4 text-emerald-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-emerald-500 dark:focus:ring-emerald-400 focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={isAllSelected ? t('list.unselectAll') : t('list.selectAll')}
                />
                <span className="whitespace-nowrap">
                  {isAllSelected ? t('list.unselectAll') : t('list.selectAll')}
                </span>
                {selectedIds.size > 0 && (
                  <span className="hidden sm:inline text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                    {selectedIds.size === 1 ? t('list.selectedCountOne', { count: String(selectedIds.size) }) : t('list.selectedCountMany', { count: String(selectedIds.size) })}
                  </span>
                )}
              </label>
            )}
          </div>
        ) : undefined}
      >
        {/* Content */}
        {displayedEntities.length === 0 ? (
          <EmptyState
            icon="📋"
            title={labels.emptyTitle}
            description={labels.emptyDescription}
            actionLabel={labels.emptyActionLabel || labels.newButton}
            onAction={() => handleOpenModal()}
          />
        ) : viewMode === 'card' ? (cardView) : enableTableCustomization ? (
          <DataTableWithCustomization
            data={displayedEntities}
            columns={resolvedTableColumns}
            keyExtractor={(row) => row.id}
            storageKey={tableCustomizationKey}
            controller={tableCustomizationController}
            showCustomizationMenu={false}
            selectable={enableBulkSelection}
            selectedIds={selectedIds}
            onToggleSelection={toggleSelection}
            onToggleAll={toggleSelectAll}
            isAllSelected={isAllSelected}
            isSomeSelected={isSomeSelected}
            isItemSelectable={isEntitySelectable}
            onRowClick={(item) => handleOpenModal(item, 'view')}
            getRowStamp={cardConfig?.getStamp ? (item) => cardConfig.getStamp!(item) : undefined}
          />
        ) : (
          <DataTable
            data={displayedEntities}
            columns={resolvedTableColumns}
            keyExtractor={(row) => row.id}
            selectable={enableBulkSelection}
            selectedIds={selectedIds}
            onToggleSelection={toggleSelection}
            onToggleAll={toggleSelectAll}
            isAllSelected={isAllSelected}
            isSomeSelected={isSomeSelected}
            isItemSelectable={isEntitySelectable}
            onRowClick={(item) => handleOpenModal(item, 'view')}
            getRowStamp={cardConfig?.getStamp ? (item) => cardConfig.getStamp!(item) : undefined}
          />
        )}
      </ListPageLayout>

      {/* Bulk Actions Bar */}
      {enableBulkSelection && permissions.canDeleteEntries && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          totalCount={selectableFilteredEntities.length}
          actions={defaultBulkActions}
          onClear={clearSelection}
          loading={submitting}
        />
      )}

      {/* Edit/Create Modal */}
      {/* Edit/Create Modal - full screen with sticky header and header save/back buttons */}
        <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={(() => {
          // Resolve a clean record name: prefer explicit recordTitle, otherwise
          // try to strip common verbs from the edit/new labels (e.g. 'Edit Category' -> 'Category')
          const stripVerb = (s?: string) => {
            if (!s) return undefined
            return s.replace(/^(Edit|New|Create|Update|Adicionar|Editar|Novo)\s+/i, '').trim()
          }

          const recordName = labels.recordTitle || stripVerb(labels.modalTitleEdit) || stripVerb(labels.modalTitleNew) || ''

          if (modalMode === 'view') {
            return `${labels.modalTitleView || 'View'} ${recordName}`.trim()
          }

          return editingEntity ? labels.modalTitleEdit : labels.modalTitleNew
        })()}
        size={isMobile ? 'xl' : 'lg'}
        fullScreen={isMobile}
        mobileTopAligned={isMobile}
        headerLeft={isMobile ? (
          modalMode === 'view' ? (
            <button
              onClick={handleCloseModal}
              className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-colors"
              title={labels.cancelButton}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          ) : (
            <button
              onClick={handleCloseModal}
              className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-colors"
              title={labels.cancelButton}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )
        ) : undefined}
        headerRight={(
          <div className="flex items-center gap-3">
            {modalMode === 'view' ? (
              <>
                {/* Actions overflow menu - view mode only */}
                {editingEntity && (
                  <OverflowMenu
                    items={[
                      ...(resolvedCanEdit(editingEntity) ? [{
                        label: cardConfig?.editLabel ?? t('actions.edit'),
                        icon: (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        ),
                        onClick: () => handleOpenModal(editingEntity, 'edit')
                      }] : []),
                      ...(extraViewActions?.(editingEntity) ?? []),
                      ...(editingShowDelete ? [{
                        label: cardConfig?.deleteLabel ?? labels.deleteButton,
                        icon: (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        ),
                        danger: true,
                        onClick: () => setConfirmDelete(editingEntity)
                      }] : [])
                    ]}
                  />
                )}

                {/* Desktop close button - after overflow menu in view mode */}
                {!isMobile && (
                  <button
                    onClick={handleCloseModal}
                    className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
                    title={labels.cancelButton}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </>
            ) : (
              <>
                {/* Save button - edit/insert mode only */}
                <button
                  onClick={() => {
                    const form = document.getElementById('entity-edit-form') as HTMLFormElement | null
                    if (form && typeof form.requestSubmit === 'function') {
                      form.requestSubmit()
                    } else if (form) {
                      form.submit()
                    }
                  }}
                  className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 transition-colors"
                  title={editingEntity ? labels.updateButton : labels.submitButton}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>

                {/* Desktop close button - edit/insert mode */}
                {!isMobile && (
                  <button
                    onClick={handleCloseModal}
                    className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
                    title={labels.cancelButton}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      >
        <form id="entity-edit-form" onSubmit={handleSubmit}>
          {renderForm(formData, setFormData, formErrors, submitting, editingEntity, modalMode)}

          {/* Footer actions removed: header provides Back/Save controls */}
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title={labels.deleteTitle}
        message={labels.deleteMessage}
        confirmText={labels.deleteButton}
        cancelText={labels.cancelButton}
        type="danger"
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        isOpen={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        onConfirm={async () => {
          await handleBulkDelete()
          setConfirmBulkDelete(false)
        }}
        title={labels.bulkDeleteTitle || t('list.bulkDeleteTitle')}
        message={labels.bulkDeleteMessage || t('list.bulkDeleteMessage', { count: String(selectedIds.size) })}
        confirmText={labels.bulkDeleteButton || t('list.bulkDeleteButton')}
        cancelText={labels.cancelButton || 'Cancelar'}
        type="danger"
      />

      {/* Advanced Filters Modal */}
      <AdvancedFiltersModal
        fields={filterFields}
        values={filterValues}
        onChange={setFilterValues}
        savedFilters={savedFilters}
        onSave={async (name, isDefault) => {
          await saveFilter(name, isDefault)
        }}
        onLoad={loadFilter}
        onDelete={deleteFilter}
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        labels={{
          buttonLabel: t('list.quickFilters.advancedTitle'),
          title: t('list.quickFilters.advancedTitle'),
          clearAll: t('list.quickFilters.clearAll'),
          apply: t('list.quickFilters.apply'),
          saveFilter: t('list.quickFilters.saveFilterButton'),
          activeFilters: 'ativos'
        }}
        namespace={namespace}
      />
    </>
  )
}
